<script lang="ts">
  /**
   * YourSetsCard — one set you've swum, short (Scott, 2026-09-11: "the
   * vertical bars aren't working"). Name + "{meters} · swum N times", three
   * tiles First / Fastest / Last (dates WITH the year — a first swim can be a
   * year back), then
   * "See your last N ›" (N = the rows the page opens with, VP round 4; "See your
   * swim ›" for a set swum once), which opens the set page (SetPageSheet), where the
   * swims sit side by side as horizontal rows. Card chrome mirrors
   * SetCompareCard so Sets and Club read as one family.
   *
   * On average-length cards each tile carries "/ length" under its number, so no
   * tile reads as a whole-set time (VP panel B7; VP round 4 put the unit on the
   * number). The meta line no longer says "avg length": VP round 5 (3 of 5) read
   * it as a stat with its number missing once the tiles carried the unit
   * (orch-9 ruled, reversing round 3).
   *
   * Swum once → the First tile only.
   *
   * Under the name, the set's strokes as last swum — one coloured tile per
   * length in swim order, a "20 × FREE" band when it is all one stroke, a
   * proportional bar once a mixed set passes 12 lengths (Scott, 2026-09-11:
   * "you should be able to see it and understand the strokes from the main
   * sets tab"). Rules in lib/setStrokeStrip.ts, pixels in SetStrokeStrip.svelte.
   */
  import type { SetHistoryRow } from "../lib/setHistory";
  import { fmtSetValue, fmtSetDate, setMetersLabel } from "../lib/setHistory";
  import { ROWS_STEP } from "../lib/setPage";
  import { showcase } from "../lib/nav";
  import { openOverlay } from "../lib/overlay";
  import { requestSetShare } from "../lib/share";
  import { haptic } from "../lib/haptics";
  import { strokeStripFor } from "../lib/setStrokeStrip";
  import SetStrokeStrip from "./SetStrokeStrip.svelte";

  interface Props {
    row: SetHistoryRow;
  }
  let { row }: Props = $props();

  /** The set's own picture, from the swim the LAST tile counts — same basis as
   *  that number. Null (so the row is exactly what it was) for a swim cached
   *  before swimFacts v5, until the background rescan gives it swim order. */
  let strip = $derived(
    // The meters ride along only when the header shows a RANGE: "20 × FREE"
    // beside "50–575 m" had readers doing arithmetic (VP round 1, B2).
    strokeStripFor(row.last.blocks, {
      rangeMeters: row.metersMin === row.metersMax ? null : row.last.meters,
    }),
  );

  let once = $derived(row.timesSwum === 1);
  // VP round 4: an average-length tile carries its unit on the number, the set page's "/ length".
  let perLength = $derived(row.basis === "perLength");
  // The set page's "Your last N" heading counts the same rows (setPage.lastSwimRows).
  let seeLabel = $derived(once ? "See your swim" : `See your last ${Math.min(ROWS_STEP, row.timesSwum)}`);
  let meta = $derived(`${setMetersLabel(row)} · ${row.timesSwum === 1 ? "swum once" : `swum ${row.timesSwum} times`}`);

  function openPage() {
    haptic("selection");
    openOverlay({ kind: "set-history", setId: row.setId });
  }

  /** Share this set's card (Scott, 06:17 2026-09-10: "share this set" from Your Sets). */
  function share() {
    haptic("light");
    requestSetShare(row.setId);
  }
</script>

<article class="set-card" aria-label={`${row.displayName}, ${meta}`}>
  <header class="set-name" class:tight={strip !== null}>
    <span class="t">{row.displayName}</span>
    <span class="d">{meta}</span>
  </header>

  {#if strip !== null}
    <SetStrokeStrip {strip} />
  {/if}

  <div class="stat3" class:stripped={strip !== null} class:solo={once}>
    <div class="tile">
      <div class="n">{fmtSetValue(row, row.first.valueS)}</div>
      {#if perLength}<div class="u">/ length</div>{/if}
      <div class="l">First</div>
      <div class="w">{fmtSetDate(row.first.dateMs)}</div>
    </div>
    {#if !once}
      <div class="tile">
        <div class="n best">{fmtSetValue(row, row.fastest.valueS)}</div>
        {#if perLength}<div class="u">/ length</div>{/if}
        <div class="l">Fastest</div>
        <div class="w">{fmtSetDate(row.fastest.dateMs)}</div>
      </div>
      <div class="tile">
        <div class="n">{fmtSetValue(row, row.last.valueS)}</div>
        {#if perLength}<div class="u">/ length</div>{/if}
        <div class="l">Last</div>
        <div class="w">{fmtSetDate(row.last.dateMs)}</div>
      </div>
    {/if}
  </div>

  <div class="foot">
    <button type="button" class="see-last" data-see-last={row.setId} onclick={openPage}>
      {seeLabel} <span aria-hidden="true">›</span>
    </button>
    {#if !$showcase}
      <button type="button" class="share-set" data-share-set={row.setId} onclick={share}>Share this set</button>
    {/if}
  </div>
</article>

<style>
  .set-card {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: 14px 16px;
    font-family: var(--font-text);
    color: var(--text-body);
    font-size: 16px;
    line-height: 1.5;
  }
  /* A long name + meta wraps the meta under the name, never the name mid-word. */
  .set-name {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
    column-gap: 8px;
    margin-bottom: 8px;
  }
  /* The stroke strip carries the gap on both sides, so the row grows by the
     strip's own 16 px and no more (+12 px against the 10 px gap it replaces). */
  .set-name.tight {
    margin-bottom: 0;
  }
  .stat3.stripped {
    margin-top: 0;
  }
  .set-name .t {
    font-weight: 600;
    font-size: 18px;
    color: var(--text-primary);
  }
  .set-name .d {
    font-size: 13px;
    color: var(--text-muted);
    white-space: nowrap;
  }

  /* First / Fastest / Last */
  .stat3 {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
  .stat3.solo .tile {
    flex: 0 1 50%;
  }
  .tile {
    flex: 1;
    min-width: 0;
    background: var(--bg-raised);
    border-radius: var(--radius-button);
    padding: 8px 10px;
  }
  .n {
    font-size: 22px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
  .n.best {
    color: var(--win);
  }
  /* The unit on the number (average-length cards only), the set page's row size. */
  .u {
    font-size: 12px;
    line-height: 14px;
    color: var(--text-muted);
  }
  .l {
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-top: 2px;
  }
  /* One line: a date never breaks in the middle. */
  .w {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 2px;
    white-space: nowrap;
  }

  /* The way into the set page, and sharing, side by side. */
  .foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 10px;
  }
  .see-last {
    min-height: 44px;
    padding: 0 4px;
    margin-left: -4px;
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font-family: var(--font-text);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .see-last:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  /* A plain, obvious button: the accent outline the share sheet's "Share a set" uses. */
  .share-set {
    min-height: 44px;
    padding: 0 14px;
    border-radius: var(--radius-button);
    border: 1px solid var(--accent-primary);
    background: transparent;
    color: var(--accent-primary);
    font-family: var(--font-text);
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .share-set:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
</style>
